import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { TunnelsOverzicht } from '../components/Tunnels.jsx';
import { api } from '../api.js';
import { t, geleden, getal } from '../i18n.js';
import { St, bronstatus } from '../components/Status.jsx';
import { NogGeenRechten } from './Meldingen.jsx';
import { AlertRijen, MeldingStoring } from './Alerts.jsx';
import ModuleTegels from '../components/ModuleTegels.jsx';
import AlertsInDeTijd from '../components/AlertsInDeTijd.jsx';

/**
 * Het overzicht. Zolang er geen alerts zijn (die komen met de detecties), is dit de stand van de
 * bronnen: wie stuurt, wie is stil, wie wacht, en wie stuurt zonder bron te zijn.
 *
 * Een nul die betekent "we ontvangen niets" en een nul die betekent "er gebeurt niets" horen er niet
 * hetzelfde uit te zien. Daarom zegt een lege stand dat er nog geen bronnen zijn.
 */
export default function Overzicht({ mij, klant }) {
  const mag = mij.permissions.includes('alerts.lezen');
  const q = useQuery({ queryKey: ['bronnen', klant], queryFn: () => api.bronnen(klant), refetchInterval: 15_000, enabled: mag });
  const al = useQuery({ queryKey: ['alerts', klant, 'open'], queryFn: () => api.alerts(klant), refetchInterval: 15_000, enabled: mag });
  const mods = useQuery({ queryKey: ['modules-overzicht'], queryFn: api.modulesOverzicht, refetchInterval: 60_000, enabled: mag });
  if (!mag) return <NogGeenRechten naam={mij.name || mij.number} />;
  const naam = klant === 'alle' ? t('Alle klanten')
    : mij.tenants.find((k) => String(k.id) === String(klant))?.name ?? t('Alle klanten');
  const kop = <div className="page-head"><div><h1>{t('Overzicht')}</h1><p className="sub">{naam}</p></div></div>;
  if (q.isPending) return <>{kop}<p className="muted">{t('Laden…')}</p></>;
  if (q.isError) return <>{kop}<div className="melder rood">{t(q.error.message)}</div></>;

  const bronnen = q.data.data.sources.filter((s) => !s.removed);
  const onbekend = q.data.data.unknown;
  if (!bronnen.length && !onbekend.length) {
    return (
      <>
        {kop}
      {mods.data && <Modulesectie lijst={mods.data.data} klant={klant} alerts={al.data?.data.alerts ?? []} />}
        <section className="card">
          <header><h2>{t('Nog geen bronnen')}</h2></header>
          <p className="soft" style={{ maxWidth: '62ch' }}>
            {t('ADTS ZERO ontvangt nog geen syslog. Zodra de eerste firewalls zijn aangesloten, staan hier de alerts, de firewalls die stil zijn gevallen en de blokkades die wachten.')}
          </p>
          <ol className="soft" style={{ marginTop: 12, paddingLeft: 20, lineHeight: 1.9 }}>
            <li>{t('Een engineer voegt de firewall toe onder Bronnen.')}</li>
            <li>{t('Op de firewall gaat syslog aan, naar zero.adts.nl; de stappen staan bij de bron.')}</li>
            <li>{t('Vanaf het eerste bericht bewaakt ADTS ZERO ook of hij blijft sturen.')}</li>
          </ol>
        </section>
      </>
    );
  }

  const met = bronnen.map((b) => ({ ...b, st: bronstatus(b) }));
  const stil = met.filter((b) => b.st.klasse === 'silent');
  const wacht = met.filter((b) => b.st.klasse === 'pending');
  const uur = bronnen.reduce((n, b) => n + b.lastHour, 0);
  const tegel = (n, label, extra = '') => <div className={`tile ${extra}`}><div className="n">{n}</div><div className="t">{label}</div></div>;

  return (
    <>
      {kop}
      {mods.data && <Modulesectie lijst={mods.data.data} klant={klant} alerts={al.data?.data.alerts ?? []} />}
      <MeldingStoring stand={al.data?.data.notifications} />
      <AlertsInDeTijd klant={klant} />
      <div className="tiles">
        <Link className={`tile link ${al.data?.data.counts.nieuw ? 'warn' : ''}`} to="/alerts">
          <div className="n">{getal(al.data?.data.counts.open ?? 0)}</div><div className="t">{t('open alerts')}</div></Link>
        {tegel(getal(bronnen.length - stil.length - wacht.length), t('bronnen sturen'))}
        {tegel(getal(stil.length), t('stil'), stil.length ? 'warn' : '')}
        {tegel(getal(uur), t('berichten het laatste uur'))}
      </div>
      <TunnelsOverzicht klant={klant} />
      <section className="card" style={{ marginBottom: 18 }}>
        <header><h2>{t('Open alerts')}</h2><span className="meta"><Link to="/alerts">{t('alle alerts')}</Link></span></header>
        {al.data ? <AlertRijen alerts={al.data.data.alerts} toonKlant={klant === 'alle'} /> : <p className="muted">{t('Laden…')}</p>}
      </section>
      <div className="grid-2">
        <section className="card">
          <header><h2>{t('Bronnen')}</h2><span className="meta"><Link to="/bronnen">{t('alle bronnen')}</Link></span></header>
          <div className="rows">
            {[...stil, ...wacht, ...met.filter((b) => !b.st.klasse)].map((b) => (
              <Link key={b.id} className="row" to={`/bronnen/${b.id}`} style={{ gridTemplateColumns: 'minmax(0,1fr) auto auto' }}>
                <span className="title">{b.name}{klant === 'alle' && <> <span className="ttag">{b.tenant.name}</span></>}</span>
                <St soort={b.st.soort}>{b.st.label}</St>
                <span className="muted">{b.status === 'wacht' ? '' : geleden(b.lastMessageAt)}</span>
              </Link>
            ))}
          </div>
        </section>
        <div className="stack">
          {onbekend.length > 0 && (
            <div className="callout warn">
              {t('{n} afzender(s) sturen syslog maar horen bij geen bron.', { n: onbekend.length })} <Link to="/bronnen">{t('Bekijken')}</Link>
            </div>
          )}
          <div className="callout">{t('ADTS ZERO bewaakt nu of elke bron blijft sturen. De andere detecties komen na de eerste week meten: per bron en per bericht-ID.')}</div>
        </div>
      </div>
    </>
  );
}

/** De modules bovenaan, naast elkaar: bij één klant zijn stand, bij alle klanten bij hoeveel ze aan staan (in de tooltip). */
function Modulesectie({ lijst, klant, alerts }) {
  const keuze = klant === 'alle' ? lijst : lijst.filter((x) => String(x.tenant.id) === String(klant));
  if (!keuze.length) return null;
  /* In gebruik: aan én ingericht (de bron of koppeling is er). ADTS heeft alles aan, dus "aan" alleen zegt bij alle klanten niets. */
  const isAan = (x, id) => x.modules.find((y) => y.id === id)?.state === 'aan';
  const inGebruik = (x, id) => isAan(x, id) && !x.modules.find((y) => y.id === id)?.steps?.some((s) => !s.ok);
  const modules = keuze[0].modules.map((m) => ({ id: m.id, label: m.label,
    aan: keuze.filter((x) => isAan(x, m.id)).length, van: klant === 'alle' ? keuze.length : undefined,
    gebruikt: keuze.some((x) => inGebruik(x, m.id)) }))
    .map((m) => (klant === 'alle' ? m : { ...m, aan: m.aan > 0 }));
  return (
    <section style={{ marginBottom: 18 }}>
      <div className="group-h"><h2 style={{ margin: 0, fontSize: 16 }}>{t('Modules')}</h2><Link to="/modules" className="muted">{t('alle modules')}</Link></div>
      <ModuleTegels modules={modules} alerts={alerts} rij />
    </section>
  );
}
